/**
 * NavRig — Canvas-scope rig selector.
 *
 * Plan 05-01 Task 1 split from NavController. NavController now lives at App
 * root so its NavSendContext.Provider wraps both Canvas and UIShell. Rig
 * mounting (which requires R3F hooks via useFrame/useThree) must stay INSIDE
 * <Canvas>, so this component reads `navState` from useNavStore (written by
 * NavController) and mounts the correct rig for the current state.
 *
 * Design notes:
 *   - Single source of truth for the state is the XState machine in
 *     NavController; this component is a pure consumer of the mirrored value
 *     in Zustand — no local useMachine.
 *   - StreetcarRig (paused) renders during `boarding` so the camera sits at
 *     the first spline waypoint (the Birmingham platform) during the 1500ms
 *     auto-advance window.
 *   - `arrivalCinematic` mounts <ArrivalCinematic /> (Plan 05-02) — a 2-second
 *     camera damp from the spline-endpoint pose to manifest.spawnPose; fires
 *     ARRIVAL_DONE via useNavSend to advance the machine into freeWalk.
 */
import { WalkRig } from './WalkRig';
import { StreetcarRig } from './StreetcarRig';
import { TourRig } from './TourRig';
import { CompareRig } from './CompareRig';
import { ArrivalCinematic } from './ArrivalCinematic';
import { useNavStore } from '../store/useNavStore';

export function NavRig() {
  const navState = useNavStore((s) => s.navState);

  switch (navState) {
    case 'freeWalk':
      return <WalkRig />;
    case 'streetcarRiding':
      return <StreetcarRig />;
    case 'boarding':
      return <StreetcarRig paused />;
    case 'arrivalCinematic':
      return <ArrivalCinematic />;
    case 'guidedTour':
      return <TourRig />;
    case 'photoCompare':
      return <CompareRig />;
    default:
      return null;
  }
}
